import React, { useEffect, useRef } from 'react';
import { useExpandableContent } from '@noths/polaris-client-utils';
import { CSSProp } from 'styled-components';

import { ExpandableToggleButton } from 'src/components/molecules/ExpandableToggleButton';
import * as styles from './styles';

export interface Props {
  children: JSX.Element;
  expandable?: boolean;
  showButton?: boolean;
  toggleContainerId: string;
  isExpandedOverride?: boolean;
  minHeight?: number;
  customFadeHeight?: number;
  styles?: CSSProp;
  onToggle?: (isOpen: boolean) => void;
}

const MINIMUM_HEIGHT_IN_PX = 423;
export const EXPANDABLE_TOGGLE_TESTID = 'expandable-toggle';

export const ExpandableToggleContainer = ({
  children,
  expandable = true,
  showButton = true,
  toggleContainerId,
  isExpandedOverride,
  minHeight = MINIMUM_HEIGHT_IN_PX,
  styles: customStyles,
  onToggle,
}: Props) => {
  const elementWrapper = useRef<HTMLDivElement | null>(null);
  const { isExpanded, height, toggleExpanded } = useExpandableContent({
    isExpandable: true,
    minHeight,
    elementWrapper,
    observeSubtree: true,
  });

  useEffect(() => {
    if (!expandable || isExpandedOverride == null) return;

    if (isExpanded !== isExpandedOverride) toggleExpanded();
  }, [expandable, isExpanded, isExpandedOverride, toggleExpanded, minHeight]);

  if (!expandable) return children;

  const handleToggleExpanded = () => {
    toggleExpanded();

    onToggle?.(!isExpanded);
  };

  return (
    <div
      css={[styles.expandingSection, styles.expandableContentWrapper, customStyles]}
      style={{ height }}
      ref={elementWrapper}
      data-is-expanded={isExpanded}
      data-testid={EXPANDABLE_TOGGLE_TESTID}
    >
      {children}
      {showButton && (
        <ExpandableToggleButton
          css={styles.expandableToggleButton}
          controls={toggleContainerId}
          toggleExpanded={handleToggleExpanded}
          isExpanded={isExpanded}
        />
      )}
    </div>
  );
};
